import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  buildImageSrc,
  transfromImageIntoFrame,
} from '@/entities/collection/model/helpers';
import {
  captureBackground,
  captureFrame,
} from '@/entities/collection/model/thunks';
import {
  CollectionState,
  DimensionsInfo,
  Frame,
  HorizontalNavigation,
  RelativeVector2d,
} from '@/entities/collection/model/types';
import { Collection, CreateImageResponse, FrameImage } from '@/shared/api';
import { SkuVariant } from '@/shared/api/types';

const initialState = (): CollectionState => {
  return {
    collectionId: null,
    dataset: null,
    mode: null,
    zoneId: null,
    startedAt: null,
    finishedAt: null,
    backgroundImage: null,
    backgroundImageId: null,
    selectedSku: null,
    skuList: [],
    skuToPoint: [],
    framesList: [],
    draftSkuList: [],
    draftSkuToPoint: [],
    selectedFrameIndex: -1,
    pending: null,
    opacity: 0.5,
    imageSize: {
      width: 0,
      height: 0,
    },
    isControlsDisabled: false,
  };
};

export const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {
    setMode(state, action: PayloadAction<CollectionState['mode']>) {
      state.mode = action.payload;
    },
    setDatasetType(state, action: PayloadAction<CollectionState['dataset']>) {
      state.dataset = action.payload;
    },
    setZone(state, action: PayloadAction<CollectionState['zoneId']>) {
      state.zoneId = action.payload;
    },

    updateImageSize(
      state,
      action: PayloadAction<CollectionState['imageSize']>
    ) {
      state.imageSize = action.payload;
    },

    resetCollection() {
      return initialState();
    },

    initCollection(
      state,
      action: PayloadAction<
        Pick<Collection, 'id' | 'mode' | 'dataset_type' | 'zone_id'>
      >
    ) {
      state.collectionId = action.payload.id;
      state.dataset = action.payload.dataset_type;
      state.mode = action.payload.mode;
      state.zoneId = action.payload.zone_id;
    },
    initFrames(
      state,
      action: PayloadAction<{
        images: FrameImage[];
        dimensions: DimensionsInfo;
      }>
    ) {
      const framesList: Frame[] = action.payload.images.map(
        transfromImageIntoFrame(action.payload.dimensions)
      );
      const lastFrameIndex = framesList.length - 1;
      state.framesList = framesList;
      state.backgroundImageId =
        action.payload.images[lastFrameIndex].background_image_id;
      state.backgroundImage = buildImageSrc(
        action.payload.images[lastFrameIndex].background_image_id
      );
      state.imageSize.width = action.payload.dimensions.videoWidth;
      state.imageSize.height = action.payload.dimensions.videoHeight;
    },

    setSelectedSku(
      state,
      action: PayloadAction<CollectionState['selectedSku']>
    ) {
      state.selectedSku = action.payload;
    },

    addSku(state, action: PayloadAction<SkuVariant>) {
      state.skuList.push(action.payload);
    },
    deleteSku(state, action: PayloadAction<{ index: number }>) {
      state.skuList.splice(action.payload.index, 1);
      state.skuToPoint.splice(action.payload.index, 1);
    },
    deleteAllSku(state) {
      state.skuList = [];
      state.skuToPoint = [];
    },
    attachPointToSku(
      state,
      action: PayloadAction<{
        variantIndex: number;
        point: RelativeVector2d | null;
      }>
    ) {
      state.skuToPoint[action.payload.variantIndex] = action.payload.point;
    },

    addFrame(state, action: PayloadAction<Frame>) {
      state.framesList.push(action.payload);
    },

    syncFrame(state, action: PayloadAction<CreateImageResponse['result']>) {
      state.framesList[state.framesList.length - 1].id = action.payload.id;
    },
    deleteFrame(state) {
      if (!state.selectedFrameIndex || state.selectedFrameIndex < 0) {
        state.framesList.pop();
      } else {
        state.framesList.splice(state.selectedFrameIndex, 1);
      }
    },

    updateFrame(state, action: PayloadAction<Partial<Frame>>) {
      state.framesList[state.selectedFrameIndex] = {
        ...state.framesList[state.selectedFrameIndex],
        ...action.payload,
      };
    },

    updateFramePoint(
      state,
      action: PayloadAction<{ variantIndex: number; point: RelativeVector2d }>
    ) {
      state.framesList[state.selectedFrameIndex].points[
        action.payload.variantIndex
      ] = action.payload.point;
    },

    deletePoint(state, action: PayloadAction<{ variantIndex: number }>) {
      const variantIndex = action.payload.variantIndex;
      if (state.selectedFrameIndex < 0) {
        if (state.mode === 'single') {
          state.skuToPoint[variantIndex] = null;
        } else {
          state.skuToPoint.splice(variantIndex, 1);
        }
      }
    },

    navigateFrame(
      state,
      action: PayloadAction<{ direction: HorizontalNavigation }>
    ) {
      if (state.selectedFrameIndex === null || state.selectedFrameIndex < 0) {
        state.selectedFrameIndex = state.framesList.length - 1;

        state.draftSkuList = state.skuList;
        state.draftSkuToPoint = state.skuToPoint;

        state.skuList = state.framesList[state.selectedFrameIndex].variants;
        state.skuToPoint = state.framesList[state.selectedFrameIndex].points;
      } else {
        const nextIndex = action.payload.direction === 'left' ? -1 : 1;
        state.selectedFrameIndex = Math.min(
          Math.max(0, state.selectedFrameIndex + nextIndex),
          state.framesList.length
        );
        state.skuList = state.framesList[state.selectedFrameIndex].variants;
        state.skuToPoint = state.framesList[state.selectedFrameIndex].points;
      }
    },

    goLive(state) {
      state.selectedFrameIndex = -1;
      state.skuList = state.draftSkuList;
      state.skuToPoint = state.draftSkuToPoint;
    },

    setOpacity(state, action: PayloadAction<number>) {
      state.opacity = action.payload;
    },

    setPending(state, action: PayloadAction<CollectionState['pending']>) {
      state.pending = action.payload;
    },

    setIsControlsDisabled(state, action: PayloadAction<boolean>) {
      state.isControlsDisabled = action.payload;
    },
  },
  selectors: {
    isStartDisabled: (state) => {
      return state.dataset === null || state.mode === null;
    },
    availableSkus: (state) =>
      state.skuList
        .map((variant, index) => ({
          ...variant,
          variantIndex: index,
        }))
        .filter((sku) => !state.skuToPoint[sku.variantIndex]),
    skusWithPoints: (state) =>
      state.skuList
        .map((variant, index) => ({
          ...variant,
          variantIndex: index,
        }))
        .filter((sku) => sku.variantIndex in state.skuToPoint),
    collectionStat: (state) =>
      state.framesList.reduce<{
        skuList: SkuVariant[];
        count: Record<string, number>;
      }>(
        (acc, frame) => {
          frame.variants.forEach((variant) => {
            if (variant.id in acc.count) {
              acc.count[variant.id] += 1;
            } else {
              acc.count[variant.id] = 1;
              acc.skuList.push(variant);
            }
          });

          return acc;
        },
        { skuList: [], count: {} }
      ),
    isReadOnly: (state) => state.selectedFrameIndex >= 0,
    selectIsControlsDisabled: (state) => state.isControlsDisabled,
  },

  extraReducers(builder) {
    builder.addCase(captureBackground.fulfilled, (state, action) => {
      state.backgroundImageId = action.payload.result.png_image_path;
      state.backgroundImage = buildImageSrc(
        action.payload.result.webp_image_path
      );
      state.pending = null;
      state.imageSize.width = action.payload.result.width;
      state.imageSize.height = action.payload.result.height;
    });
    builder.addCase(captureBackground.rejected, (state) => {
      state.pending = null;
    });
    builder.addCase(captureFrame.rejected, (state) => {
      state.pending = null;
    });
    builder.addCase(captureBackground.pending, (state) => {
      state.pending = 'background';
    });
  },
});

export const {
  setMode,
  setDatasetType,
  setSelectedSku,
  setZone,
  addSku,
  deleteAllSku,
  deleteSku,
  attachPointToSku,
  initCollection,
  initFrames,
  resetCollection,
  addFrame,
  syncFrame,
  updateFrame,
  updateFramePoint,
  deleteFrame,
  deletePoint,
  navigateFrame,
  goLive,
  setOpacity,
  setPending,
  updateImageSize,
  setIsControlsDisabled,
} = collectionSlice.actions;
export const {
  isStartDisabled,
  availableSkus,
  skusWithPoints,
  collectionStat,
  isReadOnly,
  selectIsControlsDisabled,
} = collectionSlice.selectors;
